import { AssetTypeAttributes } from '@/types'
import { UpdateAssetTypeMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditAssetTypeQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { useContext } from 'react'
import { CurrentTenantContext } from '@/core/current_tenant_context'
import { Selector as TaxRateSelector } from '@/components/tax_rate/selector'

type AssetType = EditAssetTypeQuery['assetTypes']['nodes'][0]

interface Props {
  id: string
}

export const Form = ({ id }: Props) => {
  const {
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading,
    fieldErrors,
    attributes,
    setAttributes
  } = useForm<UpdateAssetTypeMutation, AssetTypeAttributes, EditAssetTypeQuery, AssetType>({
    id,
    initializer: assetType => ({
      documentCategoryId: assetType.documentCategory?.id || '',
      manufacturer: assetType.manufacturer || '',
      model: assetType.model || '',
      taxRateId: assetType.taxRate?.id || '',
      costCents: assetType.costCents || 0,
      rateCents: assetType.rateCents || 0,
      availableToEvents: assetType.availableToEvents || false,
      displayOnWebsite: assetType.displayOnWebsite || false,
      description: assetType.description|| '',
      weight: assetType.weight || 0,
      stockCount: assetType.stockCount || 0,
      mystroTestId: assetType.mystroTestId || ''
    }),
    query: EditQuery,
    queryKey: 'assetTypes',
    mutation: UpdateMutation,
    resultKey: 'updateAssetType'
  })
  const { currentCompany } = useContext(CurrentTenantContext)

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildField({ name: 'Manufacturer', field: 'manufacturer', type: 'manufacturer' }) }
          { formBuilder.buildField({ name: 'Model', field: 'model', type: 'asset-type-model' }) }
          { formBuilder.buildField({ name: 'Weight', field: 'weight', type: 'weight' }) }
          { formBuilder.buildField({ name: 'Stock count', field: 'stockCount', type: 'non-negative-integer' }) }
        </div>
        { formBuilder.buildField({ name: 'Description', field: 'description', type: 'textarea' }) }
        { currentCompany?.mystroEnabled && formBuilder.buildField({ name: 'Default Mystro test', field: 'mystroTestId', type: 'mystro-test' }) }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildField({ name: 'Replacement cost', field: 'costCents', type: 'currency' }) }
          { formBuilder.buildField({ name: 'Base rate', field: 'rateCents', type: 'currency' }) }
        </div>
        {
          currentCompany && currentCompany.vatNumber && formBuilder.buildCustomField({
            name: 'Tax rate',
            field: 'taxRateId',
            input: <TaxRateSelector
              invalid={!!fieldErrors?.taxRateId}
              value={attributes.taxRateId}
              onChange={value => setAttributes({ ...attributes, taxRateId: value?.id || '' })}
            />
          })
        }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildField({ name: 'Display on your website', field: 'displayOnWebsite', type: 'switch' }) }
          { formBuilder.buildField({ name: 'Available to events', field: 'availableToEvents', type: 'switch' }) }
        </div>
        { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
      </div>
    </>
  )
}
